import { useMemo } from 'react';

import { ReimbursementType } from '@dreryk/egabinet-prescription-frontend-api';
import { VisitHistoryPrescription } from '@dreryk/egabinet-visit-frontend-api';

import { assertUnreachable, integerFormatter, Translate } from '@dreryk/react-common';
import {
   AlignItems,
   createDynamicColumnsWidth,
   DataConfiguration,
   EmptyStatePlaceholder,
   JustifyContent,
   Table,
   TableColumnConfiguration,
   TextPlaceholder,
   ValueRendererProps,
} from '@dreryk/react-components';

import {
   MedicineDetailsModalOpenAction,
   MedicineName,
   MedicinePackage,
   useFormatStages,
} from '~libs.desktop.shared.medicine';
import { formatPreciseDosage } from '~libs.shared.prescription';

import { MagistralDrugHeading } from '../features/prescriptions/magistralDrug/MagistralDrugHeading';
import { MagistralDrugSubheading } from '../features/prescriptions/magistralDrug/MagistralDrugSubheading';
import { SentPrescriptionStatus } from '../features/prescriptions/prescriptions/sentPrescriptionPackages/SentPrescriptionStatus';
import { visitHistoryTranslationKeys } from '../translations';

const useVisitHistoryPrescriptionsDataConfiguration = (
   data: VisitHistoryPrescription[]
): DataConfiguration<VisitHistoryPrescription> => {
   return useMemo(() => ({ data: data }), [data]);
};

const useVisitHistoryPrescriptionsColumnsConfiguration =
   // eslint-disable-next-line max-lines-per-function
   (): TableColumnConfiguration<VisitHistoryPrescription>[] => {
      const formatStages = useFormatStages();

      // eslint-disable-next-line max-lines-per-function
      return useMemo<TableColumnConfiguration<VisitHistoryPrescription>[]>(() => {
         const columnsWidth = createDynamicColumnsWidth({
            commercialName: '60%',
            dosage: '40%',
            quantity: '60px',
            reimbursementType: '60px',
            accessCode: '56px',
         });

         return [
            {
               name: 'commercialName',
               label: visitHistoryTranslationKeys.prescriptionsTable.commercialName,
               width: columnsWidth.commercialName,
               accessor: (prescription) => prescription,
               sortingDisabled: true,
               justifyContent: JustifyContent.START,
               alignItems: AlignItems.CENTER,

               renderer: ({
                  value: { medicine, magistralDrug, annulled },
               }: ValueRendererProps<VisitHistoryPrescription, VisitHistoryPrescription>): JSX.Element | null => {
                  if (medicine) {
                     return (
                        <div>
                           {annulled ? <SentPrescriptionStatus /> : null}
                           <MedicineDetailsModalOpenAction
                              medicine={medicine}
                              render={(openModal) => (
                                 <MedicineName name={medicine.fullName} atc={medicine.atc} onClick={openModal} />
                              )}
                           />
                           <MedicinePackage
                              dose={medicine.dose}
                              form={medicine.form}
                              packageContent={medicine.packageContent}
                           />
                        </div>
                     );
                  }

                  if (magistralDrug) {
                     return (
                        <div>
                           <MagistralDrugHeading
                              name={magistralDrug.name}
                              magistralDrugMedicineRawMaterials={magistralDrug.rawMaterials}
                           />
                           <MagistralDrugSubheading
                              name={magistralDrug.name}
                              magistralDrugMedicineRawMaterials={magistralDrug.rawMaterials}
                              additionalDetails={magistralDrug.additionalDetails}
                              form={magistralDrug.form}
                           />
                        </div>
                     );
                  }

                  return assertUnreachable(undefined);
               },
            },
            {
               name: 'dosage',
               label: visitHistoryTranslationKeys.prescriptionsTable.dosage,
               width: columnsWidth.dosage,
               accessor: (prescription) => {
                  const extendedDosageActive = Boolean(prescription.extendedDosage?.stages.length);

                  return [
                     ...formatStages(
                        prescription.extendedDosage?.stages ?? [],
                        prescription.extendedDosage?.portionUnit
                     ),
                     !extendedDosageActive && prescription.preciseDosage
                        ? formatPreciseDosage(prescription.preciseDosage)
                        : null,
                     prescription.dosage,
                  ]
                     .filter(Boolean)
                     .join(', ');
               },
               sortingDisabled: true,
               justifyContent: JustifyContent.START,
               alignItems: AlignItems.CENTER,
            },
            {
               name: 'quantity',
               label: visitHistoryTranslationKeys.prescriptionsTable.quantity,
               width: columnsWidth.quantity,
               sortingDisabled: true,
               justifyContent: JustifyContent.START,
               alignItems: AlignItems.CENTER,
               accessor: 'quantity',
               formatter: integerFormatter,
            },
            {
               name: 'reimbursementType',
               label: visitHistoryTranslationKeys.prescriptionsTable.reimbursementType,
               width: columnsWidth.reimbursementType,
               sortingDisabled: true,
               justifyContent: JustifyContent.START,
               alignItems: AlignItems.CENTER,
               accessor: (prescription) => prescription.reimbursementType,

               renderer: ({
                  value: reimbursementType,
               }: ValueRendererProps<VisitHistoryPrescription, ReimbursementType>): JSX.Element | null => {
                  if (!reimbursementType) {
                     return null;
                  }

                  return (
                     <Translate>
                        {visitHistoryTranslationKeys.prescriptionsTable.reimbursements[reimbursementType]}
                     </Translate>
                  );
               },
            },
            {
               name: 'accessCode',
               label: visitHistoryTranslationKeys.prescriptionsTable.accessCode,
               width: columnsWidth.accessCode,
               sortingDisabled: true,
               alignItems: AlignItems.CENTER,
               accessor: 'accessCode',
               placeholder: TextPlaceholder.NO_DATA,
            },
         ];
      }, [formatStages]);
   };

type VisitHistoryPrescriptionsTableProps = {
   readonly prescriptions: VisitHistoryPrescription[];
};

export const VisitHistoryPrescriptionsTable = (props: VisitHistoryPrescriptionsTableProps): JSX.Element => {
   return (
      <Table
         testId={'prescriptions'}
         dataConfiguration={useVisitHistoryPrescriptionsDataConfiguration(props.prescriptions)}
         columns={useVisitHistoryPrescriptionsColumnsConfiguration()}
         keyExtractor={'id'}
         pageSize={1000}
         placeholder={
            <EmptyStatePlaceholder
               testId={'noPrescriptions'}
               heading={visitHistoryTranslationKeys.prescriptionsTable.noPrescriptions}
               subheading={visitHistoryTranslationKeys.prescriptionsTable.noPrescriptionsWereIssued}
            />
         }
      />
   );
};
